import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/infrastructure/service/api.service';
import {
  LegalControlQueueClassificationCounters,
  LegalControlQueueConfirmRequest,
  LegalControlQueueCounters,
  LegalControlQueueDetail,
  LegalControlQueueFilters,
  LegalControlQueueListItem,
  LegalControlQueueMonitoredTermRequest,
  LegalControlQueueMonitoredTerms,
  LegalControlQueueReviewRequest,
  LegalControlQueueSettings,
  LegalControlQueueSettingsPatchField
} from '../domain/legal-control-queue';

@Injectable({
  providedIn: 'root'
})
export class LegalControlQueueService extends ApiService {

  private readonly serviceUrl = 'legal-control-queue';

  constructor(http: HttpClient) {
    super(http);
  }

  findByFilters(filters: LegalControlQueueFilters = {}): Promise<LegalControlQueueListItem[]> {
    const params = this.buildFilterParams(filters, true, true);

    return this.get(this.serviceUrl, params).toPromise();
  }

  countByClassification(filters: LegalControlQueueFilters = {}): Promise<LegalControlQueueClassificationCounters> {
    const params = this.buildFilterParams(filters, false, false);

    return this.get(`${this.serviceUrl}/classification-counters`, params).toPromise();
  }

  countByReviewStatus(filters: LegalControlQueueFilters = {}): Promise<LegalControlQueueCounters> {
    const params = this.buildFilterParams(filters, false, false);

    return this.get(`${this.serviceUrl}/counters`, params).toPromise();
  }

  findById(id: string): Promise<LegalControlQueueDetail> {
    return this.get(`${this.serviceUrl}/${id}`).toPromise();
  }

  findSettings(): Promise<LegalControlQueueSettings> {
    return this.get(`${this.serviceUrl}/settings`).toPromise();
  }

  patchSettings(fields: LegalControlQueueSettingsPatchField[]): Promise<LegalControlQueueSettings> {
    return this.patch(`${this.serviceUrl}/settings`, fields).toPromise();
  }

  findMonitoredTerms(): Promise<LegalControlQueueMonitoredTerms> {
    return this.get(`${this.serviceUrl}/settings/monitored-terms`).toPromise();
  }

  createMonitoredTerm(request: LegalControlQueueMonitoredTermRequest): Promise<LegalControlQueueMonitoredTerms> {
    return this.post(`${this.serviceUrl}/settings/monitored-terms`, request).toPromise();
  }

  deleteMonitoredTerm(id: string): Promise<LegalControlQueueMonitoredTerms> {
    return this.delete(`${this.serviceUrl}/settings/monitored-terms/${id}`).toPromise();
  }

  markAsRead(id: string): Promise<LegalControlQueueDetail> {
    return this.patch(`${this.serviceUrl}/${id}/read`).toPromise();
  }

  updateSourceProject(id: string, projectId: string): Promise<LegalControlQueueDetail> {
    return this.patch(`${this.serviceUrl}/${id}/source-project`, { projectId }).toPromise();
  }

  requestSuggestion(id: string): Promise<LegalControlQueueDetail> {
    return this.post(`${this.serviceUrl}/${id}/ai-suggestion`).toPromise();
  }

  updateReviewedSuggestion(id: string, request: LegalControlQueueReviewRequest): Promise<LegalControlQueueDetail> {
    return this.put(`${this.serviceUrl}/${id}/reviewed-suggestion`, request).toPromise();
  }

  markAsReviewed(id: string, request?: LegalControlQueueReviewRequest): Promise<LegalControlQueueDetail> {
    return this.patch(`${this.serviceUrl}/${id}/reviewed`, request).toPromise();
  }

  discard(id: string): Promise<LegalControlQueueDetail> {
    return this.patch(`${this.serviceUrl}/${id}/discard`).toPromise();
  }

  confirm(id: string, request: LegalControlQueueConfirmRequest): Promise<LegalControlQueueDetail> {
    return this.post(`${this.serviceUrl}/${id}/confirm`, request).toPromise();
  }

  private buildFilterParams(filters: LegalControlQueueFilters, includeClassificationGroup: boolean, includeSortDirection: boolean): HttpParams {
    let params = new HttpParams().set('unreadOnly', `${filters.unreadOnly ?? true}`);

    (filters.statuses || []).forEach(status => {
      params = params.append('status', status);
    });

    if (filters.text?.trim()) {
      params = params.set('text', filters.text.trim());
    }

    if (filters.projectId) {
      params = params.set('projectId', filters.projectId);
    }

    if (filters.activityDateStart) {
      params = params.set('activityDateStart', filters.activityDateStart);
    }

    if (filters.activityDateEnd) {
      params = params.set('activityDateEnd', filters.activityDateEnd);
    }

    if (includeClassificationGroup && filters.classificationGroup) {
      params = params.set('classificationGroup', filters.classificationGroup);
    }

    if (includeSortDirection && filters.sortDirection) {
      params = params.set('sortDirection', filters.sortDirection);
    }

    return params;
  }
}
